import "./DashDetails.css";
import React, {useEffect, useState} from "react";
import {Tooltip} from '@mui/material';
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import {fetchFlaggedSpas, fetchSpasByGuids} from "../../../redux/actions/spas.actions";
import { flagSpa } from "../../../redux/actions/spa.actions";
import { useNavigate } from "react-router-dom";
import { MQTT_PREFIX } from "../../../services/mqtt/mqttconfig";
import { AlertIcon, ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon, FlagLineIcon, MessageIcon, SlidersIcon, ThermometerIcon } from "../../../components/common/Icons/Icons";
import { localizeSpa } from "../../../utils/brand";

// Each list's colour + icon: used on its tab, count and the row hover bar
const sectionAccents: { [key: string]: { tone: string, icon: React.ReactNode } } = {
  errors: { tone: 'errors', icon: <AlertIcon/> },
  temp: { tone: 'temp', icon: <ThermometerIcon/> },
  manual: { tone: 'manual', icon: <SlidersIcon/> },
  flagged: { tone: 'flagged', icon: <FlagLineIcon/> },
};

const serialOf = (spa: any): string | null => spa?.blue_falls_serial_number ?? spa?.arctic_serial_number ?? null;

// Nickname, else the serial number, else the start of the GUID
const displayName = (spa: any): string => {
  if (spa?.nick_name) return spa.nick_name;
  const serial = serialOf(spa);
  if (serial) return String(serial);
  return spa?.id ? `${String(spa.id).slice(0, 8)}…` : 'Unknown';
};

interface ListSection {
  key: string,
  title: string,
  count?: number,
}

interface Props {
  section:string,
  open: boolean,
  setOpen: any,
  guids:any[],
  sections: ListSection[],
  onSectionChange: (key: string) => void,
}

const DashDetails: React.FC<Props> = ({section, open, setOpen, guids, sections, onSectionChange}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {data:spas,loading, meta, error} = useSelector((state: RootState) => state.spas);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (open) {
      section==='flagged' ? dispatch(fetchFlaggedSpas(`?page=${page}`)) : dispatch(fetchSpasByGuids({page: `?page=${page}`,guids:guids}));
    }
  }, [open,page,section,guids,dispatch]);

  const handleClose = () => {
    setPage(1);
    setOpen(false);
  }

  const handleFlag = async (guid:string) => {
    await dispatch(flagSpa(guid));
  }

  const handleTab = (key: string) => {
    if (key === section) return;
    setPage(1);
    onSectionChange(key);
  }


  const isBeta = MQTT_PREFIX.includes('beta');

  const isColdTub = (() => {
    const brand = localStorage.getItem('data-brand');
    if (brand) return brand === 'coldtub';
    return window.location.hostname.includes('mycoldtub');
  })();

  const redirectDomain = isColdTub ? 'mycoldtub.com' : 'myarcticspa.com';

  const redirectSiteUrl = isBeta
  ? `beta.${redirectDomain}/dealers`
  : `${redirectDomain}/dealers`;

  const classicUrl = (id: string) => `https://${redirectSiteUrl}/spadetails.aspx?SpaGUID=${id}&ignore_redirect=true`;

  // The whole row opens the spa; its own links and buttons keep doing their job
  const handleRowClick = (e: React.MouseEvent, spa: any) => {
    if ((e.target as HTMLElement).closest('a, button')) return;
    if (spa?.is_moved) navigate(`/spa/${spa.id}`);
    else window.open(classicUrl(spa.id), '_blank', 'noopener,noreferrer');
  }

  const totalPages = meta?.total_pages ?? 1;
  const total = meta?.total ?? 0;

  return (
    <div className={`dd tone-${sectionAccents[section]?.tone ?? 'flagged'}`}>
      <div className="dd-head">
        <button type="button" className="btn btn-ghost icon-btn dd-back" onClick={handleClose} aria-label="Back to dashboard">
          <ChevronLeftIcon/>
        </button>
        <div className="dd-tabs" role="tablist" aria-label="Spa lists">
          {sections.map((s) => (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={s.key === section}
              className={`dd-tab tone-${sectionAccents[s.key]?.tone ?? 'flagged'} ${s.key === section ? 'active' : ''}`}
              onClick={() => handleTab(s.key)}
            >
              <span className="dd-tab-icon" aria-hidden="true">{sectionAccents[s.key]?.icon}</span>
              {localizeSpa(s.title)}
              {typeof s.count === 'number' && <span className="dd-tab-count">{s.count}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="dd-body">
      {loading ? (
          <div className="dd-state"><span className="spinner" role="progressbar" aria-label="Loading spas"/></div>
        ):
        (spas.length===0 || error?
          <div className="dd-state">
            <p className="dd-empty-title">{localizeSpa("No Spas found")}</p>
            <p className="dd-empty-text">Nothing needs attention here right now.</p>
          </div>
        :
        <table className="dd-table">
          <thead>
            <tr>
              <th scope="col">{localizeSpa("Spa")}</th>
              <th scope="col">Owner</th>
              <th scope="col" className="dd-center">Flag</th>
              <th scope="col" className="dd-center">Comments</th>
              <th className="dd-open-cell" aria-hidden="true"/>
            </tr>
          </thead>

          <tbody>
            {spas.map((spa:any)=>
              <tr
                key={spa.id}
                className={[
                  spa?.is_moved ? '' : 'v1',
                  spa?.is_flagged ? 'flagged' : '',
                  (spa?.unread_count ?? 0) > 0 ? 'unread' : '',
                ].filter(Boolean).join(' ')}
                onClick={(e) => handleRowClick(e, spa)}
              >
                <td>
                  <div className="dd-spa">
                    {spa?.is_moved ?
                    <a
                      className="dd-spa-name"
                      href={`/spa/${spa.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/spa/${spa.id}`);
                      }}
                    >
                      {displayName(spa)}
                    </a>
                    :
                    <a
                      className="dd-spa-name"
                      href={classicUrl(spa?.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Opens the classic dealer site"
                    >
                      {displayName(spa)}<ExternalLinkIcon/>
                    </a>}
                    {!spa?.is_moved && <span className="dd-v1">V1</span>}
                  </div>
                  <span className="dd-sub">
                    {spa?.nick_name && serialOf(spa) && <span className="dd-serial">{serialOf(spa)}</span>}
                    <span className="dd-guid">{spa?.id}</span>
                  </span>
                </td>

                <td>
                  {spa.user_id ?
                  <a
                    className="dd-link"
                    href={`/user/${spa.user_id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/user/${spa.user_id}`);
                    }}
                  >
                    {spa.user?.name??"N/A"}
                  </a>
                  : <span className="dd-muted">{spa.user?.name??"N/A"}</span>}
                </td>

                <td className="dd-center">
                  <button
                    type="button"
                    className={`btn btn-ghost icon-btn dd-icon-btn ${spa?.is_flagged ? 'flagged' : ''}`}
                    onClick={()=>handleFlag(spa?.id)}
                    aria-label={spa?.is_flagged ? 'Unflag spa' : 'Flag spa'}
                    aria-pressed={Boolean(spa?.is_flagged)}
                  >
                    <FlagLineIcon filled={Boolean(spa?.is_flagged)}/>
                  </button>
                </td>

                <td className="dd-center">
                  <Tooltip title={`${spa?.comments_count ?? 0} comments · ${spa?.unread_count ?? 0} unread · ${spa?.unresolved_count ?? 0} unresolved`} arrow>
                    <a
                      href={`/spa/${spa?.id}/comments`}
                      className="btn btn-ghost icon-btn dd-icon-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/spa/${spa?.id}/comments`);
                      }}
                      aria-label="View comments"
                    >
                      <MessageIcon/>
                      {(spa?.unread_count ?? 0) > 0 && <span className="dd-badge">{spa.unread_count}</span>}
                    </a>
                  </Tooltip>
                </td>

                {/* Hover affordance: the row itself is the click target */}
                <td className="dd-open-cell" aria-hidden="true">
                  <span className="dd-open">{spa?.is_moved ? <>Open <ChevronRightIcon/></> : <>Classic <ExternalLinkIcon/></>}</span>
                </td>
              </tr>
              )}
          </tbody>
        </table>
        )}
      </div>

      <div className="dd-foot">
        <span className="dd-showing">Showing {loading ? '…' : spas?.length??0} of {total} {localizeSpa(total === 1 ? 'Spa' : 'Spas')}</span>
        {totalPages > 1 &&
          <nav className="dd-pager" aria-label="Pages">
            <button type="button" className="btn btn-sm" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>
              <ChevronLeftIcon/> Previous
            </button>
            <span className="dd-pager-status">Page {page} of {totalPages}</span>
            <button type="button" className="btn btn-sm" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>
              Next <ChevronRightIcon/>
            </button>
          </nav>}
      </div>
    </div>
);
};

export default DashDetails;
